import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ShieldCheck, KeyRound, Copy, CheckCircle2, ExternalLink, Upload, Link2,
  RefreshCw, Save, AlertTriangle, Loader2, FileText, PlusCircle, XCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  getTenantSsoConfig, updateTenantSsoConfig, parseIdpMetadata,
} from '@/services/admin';

const ATTR_FIELDS = [
  { key: 'email',      label: 'Email',       required: true,  hint: 'Beskar uses this for the user\u2019s login identity.' },
  { key: 'full_name',  label: 'Full name',   required: false, hint: 'Optional. Falls back to first+last name below.' },
  { key: 'first_name', label: 'First name',  required: false, hint: 'Combined with last name when full_name is missing.' },
  { key: 'last_name',  label: 'Last name',   required: false, hint: 'Combined with first name when full_name is missing.' },
  { key: 'role',       label: 'Role',        required: false, hint: 'Reserved for future role sync (advisory).' },
  { key: 'groups',     label: 'Groups',      required: false, hint: 'Reserved for future group sync (advisory).' },
];

const CopyChip = ({ value, label, testid }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={async () => {
        try { await navigator.clipboard.writeText(value); } catch (e) { /* ignore */ }
        setCopied(true);
        toast.success(`${label} copied`);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="inline-flex items-center gap-1 text-xs rounded-md border px-2 py-1 hover:bg-muted transition"
      data-testid={testid}
    >
      {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
      Copy
    </button>
  );
};

const AttributeMappingEditor = ({ mapping, defaults, onChange }) => {
  const setField = (key, arr) => onChange({ ...mapping, [key]: arr });
  return (
    <div className="space-y-4">
      {ATTR_FIELDS.map(({ key, label, required, hint }) => {
        const value = Array.isArray(mapping[key]) ? mapping[key] : [];
        return (
          <div key={key} className="rounded-lg border p-3 bg-slate-50/40" data-testid={`attr-row-${key}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <div className="text-sm font-semibold flex items-center gap-2">
                  {label}
                  {required && <Badge variant="destructive" className="text-[10px]">Required</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">{hint}</p>
              </div>
              <Button
                type="button" size="sm" variant="ghost"
                onClick={() => setField(key, defaults[key] || [])}
                data-testid={`attr-reset-${key}`}
              >
                <RefreshCw className="w-3 h-3 mr-1" />Reset
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {value.map((token, i) => (
                <span key={`${token}-${i}`} className="inline-flex items-center gap-1 rounded-full bg-white border px-2 py-0.5 text-xs">
                  <code className="text-[11px] break-all max-w-[24rem] truncate">{token}</code>
                  <button
                    type="button"
                    onClick={() => setField(key, value.filter((_, j) => j !== i))}
                    className="text-muted-foreground hover:text-red-500"
                    data-testid={`attr-remove-${key}-${i}`}
                  >
                    <XCircle className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <TokenAdder
                onAdd={(v) => v && setField(key, [...value, v])}
                testid={`attr-add-${key}`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TokenAdder = ({ onAdd, testid }) => {
  const [v, setV] = useState('');
  return (
    <span className="inline-flex items-center gap-1">
      <Input
        value={v}
        onChange={(e) => setV(e.target.value)}
        placeholder="Add SAML attribute (e.g. mail)"
        className="h-7 text-xs w-56"
        data-testid={`${testid}-input`}
      />
      <Button
        type="button" size="sm" variant="outline" className="h-7 px-2"
        onClick={() => { onAdd(v.trim()); setV(''); }}
        data-testid={testid}
      >
        <PlusCircle className="w-3.5 h-3.5" />
      </Button>
    </span>
  );
};

const SamlSsoConfig = ({ tenantId, tenantName }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [metaSource, setMetaSource] = useState('url'); // url | xml | manual
  const [metaValue, setMetaValue] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTenantSsoConfig(tenantId);
      setCfg(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to load SSO config';
      toast.error(msg);
      setCfg({ error: msg });
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const patch = (updates) => setCfg((c) => ({ ...c, ...updates }));

  const handleParse = async () => {
    if (metaSource === 'manual') return;
    if (!metaValue.trim()) {
      toast.error(metaSource === 'url' ? 'Paste an IdP metadata URL' : 'Paste the IdP metadata XML');
      return;
    }
    setParsing(true);
    try {
      const parsed = await parseIdpMetadata(tenantId, { source: metaSource, value: metaValue });
      patch(parsed);
      toast.success('IdP metadata parsed — review the fields below and Save.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to parse IdP metadata');
    } finally {
      setParsing(false);
    }
  };

  const handleUploadXml = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Strict XML-only guard: check extension + mime hints.
    const name = f.name.toLowerCase();
    const isXml =
      name.endsWith('.xml') ||
      /xml/i.test(f.type || '') ||
      /metadata/i.test(name);
    if (!isXml) {
      toast.error('Only .xml IdP metadata files are supported.');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const xml = String(reader.result || '');
      if (!xml.trim().startsWith('<')) {
        toast.error('File does not look like XML — please upload a valid IdP metadata file.');
        return;
      }
      setMetaSource('xml');
      setMetaValue(xml);
      // Auto-parse immediately so the owner does not need a second click.
      setParsing(true);
      try {
        const parsed = await parseIdpMetadata(tenantId, { source: 'xml', value: xml });
        patch(parsed);
        toast.success(`Loaded ${f.name} — IdP fields imported. Review and Save.`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Uploaded, but failed to parse. Edit fields manually.');
      } finally {
        setParsing(false);
      }
    };
    reader.readAsText(f);
    // Reset the input so re-uploading the same file re-triggers onChange.
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!cfg.enabled,
        idp_entity_id: cfg.idp_entity_id || '',
        idp_sso_url: cfg.idp_sso_url || '',
        idp_slo_url: cfg.idp_slo_url || null,
        idp_x509_cert_pem: cfg.idp_x509_cert_pem || '',
        name_id_format: cfg.name_id_format || 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
        allowed_email_domains: cfg.allowed_email_domains || [],
        attribute_mapping: cfg.attribute_mapping || cfg.default_attribute_mapping || {},
        jit_enabled: !!cfg.jit_enabled,
        jit_default_role: cfg.jit_default_role || 'viewer',
      };
      const next = await updateTenantSsoConfig(tenantId, payload);
      setCfg(next);
      toast.success(payload.enabled ? 'SSO configuration saved & enabled' : 'SSO configuration saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save SSO config');
    } finally {
      setSaving(false);
    }
  };

  const emailDomains = useMemo(() => (cfg?.allowed_email_domains || []).join(', '), [cfg]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="sso-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (cfg?.error) {
    return (
      <Card>
        <CardContent className="p-6 flex items-start gap-3 text-sm">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
          <div>
            <p className="font-semibold">Cannot load SSO config</p>
            <p className="text-muted-foreground">{cfg.error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenant-sso-panel">
      {/* Master enable switch */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-indigo-600" />
              Single Sign-On (SAML 2.0)
            </CardTitle>
            <CardDescription>
              Connect a custom Identity Provider so your team signs into {tenantName} with your corporate IdP.
              Users hitting <code className="text-xs">{cfg.sp_login_url}</code> are redirected to your IdP.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-muted-foreground">
              {cfg.enabled ? 'Enabled' : 'Disabled'}
            </span>
            <Switch
              checked={!!cfg.enabled}
              onCheckedChange={(v) => patch({ enabled: v })}
              data-testid="sso-enabled-switch"
            />
          </div>
        </CardHeader>
      </Card>

      {/* SP metadata block (read-only, tenant admin pastes into IdP) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-slate-500" />
            Beskar Service Provider details
          </CardTitle>
          <CardDescription>
            Paste these into your IdP&apos;s SAML application setup (Google Workspace, Azure AD, Okta, ADFS, etc.).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            ['ACS URL (Reply URL)', cfg.sp_acs_url, 'sp-acs-url'],
            ['Entity ID (Audience)', cfg.sp_entity_id, 'sp-entity-id'],
            ['SP Metadata XML',    cfg.sp_metadata_url, 'sp-metadata-url'],
            ['SP Login URL',       cfg.sp_login_url, 'sp-login-url'],
            ['SP Public Key (PEM)',cfg.sp_public_key_url, 'sp-public-key-url'],
          ].map(([label, value, tid]) => (
            <div key={tid} className="flex items-center gap-3">
              <div className="w-52 text-xs text-muted-foreground shrink-0">{label}</div>
              <code className="flex-1 text-xs bg-slate-50 border rounded px-2 py-1 truncate" data-testid={tid}>
                {value || '—'}
              </code>
              {value && (
                <>
                  <a href={value} target="_blank" rel="noreferrer">
                    <Button type="button" size="sm" variant="ghost" className="h-7 px-2">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                  </a>
                  <CopyChip value={value} label={label} testid={`copy-${tid}`} />
                </>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* IdP config import */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-500" />
            IdP configuration
          </CardTitle>
          <CardDescription>
            Import your IdP metadata via URL, XML upload, or fill the fields manually.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Prominent XML file drop zone — one-click upload */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => fileRef.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2','ring-indigo-400'); }}
            onDragLeave={(e) => { e.currentTarget.classList.remove('ring-2','ring-indigo-400'); }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('ring-2','ring-indigo-400');
              const f = e.dataTransfer.files?.[0];
              if (f && fileRef.current) {
                const dt = new DataTransfer();
                dt.items.add(f);
                fileRef.current.files = dt.files;
                handleUploadXml({ target: fileRef.current });
              }
            }}
            className="flex items-center justify-between gap-4 rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50/40 px-5 py-4 cursor-pointer hover:bg-indigo-50 transition"
            data-testid="idp-metadata-dropzone"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white border flex items-center justify-center shrink-0">
                <Upload className="w-5 h-5 text-indigo-500" />
              </div>
              <div>
                <p className="text-sm font-semibold">Upload IdP metadata XML</p>
                <p className="text-xs text-muted-foreground">
                  Drag &amp; drop or click to choose a <code className="text-[11px]">.xml</code> file exported from your IdP. We&apos;ll parse it automatically.
                </p>
              </div>
            </div>
            {parsing ? (
              <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
            ) : (
              <span className="text-xs font-medium text-indigo-600 border border-indigo-200 rounded-full px-3 py-1">Choose file</span>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".xml,application/xml,text/xml"
            onChange={handleUploadXml}
            className="hidden"
            data-testid="idp-metadata-xml-file-input"
          />

          <div className="flex flex-wrap gap-2">
            {[
              { v: 'url', label: 'Metadata URL', icon: Link2 },
              { v: 'xml', label: 'Paste XML',    icon: FileText },
              { v: 'manual', label: 'Manual fields', icon: KeyRound },
            ].map(({ v, label, icon: Icon }) => (
              <Button
                key={v} type="button" size="sm"
                variant={metaSource === v ? 'default' : 'outline'}
                onClick={() => setMetaSource(v)}
                data-testid={`meta-source-${v}`}
              >
                <Icon className="w-3.5 h-3.5 mr-1" />{label}
              </Button>
            ))}
          </div>

          {metaSource === 'url' && (
            <div className="space-y-2">
              <Label>IdP metadata URL</Label>
              <div className="flex gap-2">
                <Input
                  value={metaValue}
                  onChange={(e) => setMetaValue(e.target.value)}
                  placeholder="https://login.microsoftonline.com/{tenant}/federationmetadata/..."
                  data-testid="meta-url-input"
                />
                <Button type="button" onClick={handleParse} disabled={parsing} data-testid="parse-url-btn">
                  {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fetch & parse'}
                </Button>
              </div>
            </div>
          )}

          {metaSource === 'xml' && (
            <div className="space-y-2">
              <Label>IdP metadata XML</Label>
              <Textarea
                value={metaValue}
                onChange={(e) => setMetaValue(e.target.value)}
                rows={8}
                placeholder="<md:EntityDescriptor ...>...</md:EntityDescriptor>"
                className="font-mono text-xs"
                data-testid="meta-xml-textarea"
              />
              <Button type="button" onClick={handleParse} disabled={parsing} data-testid="parse-xml-btn">
                {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Parse XML'}
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>IdP Entity ID</Label>
              <Input
                value={cfg.idp_entity_id || ''}
                onChange={(e) => patch({ idp_entity_id: e.target.value })}
                placeholder="https://sts.example.com/..."
                data-testid="idp-entity-id-input"
              />
            </div>
            <div className="space-y-1">
              <Label>IdP SSO URL</Label>
              <Input
                value={cfg.idp_sso_url || ''}
                onChange={(e) => patch({ idp_sso_url: e.target.value })}
                placeholder="https://sts.example.com/adfs/ls/"
                data-testid="idp-sso-url-input"
              />
            </div>
            <div className="space-y-1">
              <Label>IdP SLO URL (optional)</Label>
              <Input
                value={cfg.idp_slo_url || ''}
                onChange={(e) => patch({ idp_slo_url: e.target.value })}
                placeholder="https://sts.example.com/adfs/ls/logout"
                data-testid="idp-slo-url-input"
              />
            </div>
            <div className="space-y-1">
              <Label>NameID format</Label>
              <Input
                value={cfg.name_id_format || ''}
                onChange={(e) => patch({ name_id_format: e.target.value })}
                data-testid="idp-nameid-format-input"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label>IdP X.509 signing certificate (PEM)</Label>
            <Textarea
              value={cfg.idp_x509_cert_pem || ''}
              onChange={(e) => patch({ idp_x509_cert_pem: e.target.value })}
              rows={6}
              className="font-mono text-xs"
              placeholder="-----BEGIN CERTIFICATE-----\nMIIC...\n-----END CERTIFICATE-----"
              data-testid="idp-cert-textarea"
            />
          </div>

          <div className="space-y-1">
            <Label>Allowed email domains (optional, comma-separated)</Label>
            <Input
              value={emailDomains}
              onChange={(e) => patch({
                allowed_email_domains: e.target.value
                  .split(',').map((s) => s.trim().toLowerCase()).filter(Boolean),
              })}
              placeholder="example.com, corp.example.com"
              data-testid="allowed-domains-input"
            />
            <p className="text-xs text-muted-foreground">
              Only users whose email ends with one of these domains can sign in via SSO. Leave blank to allow any domain.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* JIT + role */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Just-in-Time provisioning</CardTitle>
          <CardDescription>
            Automatically create Beskar accounts for users on their first SSO login.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3 bg-slate-50/40">
            <div>
              <p className="text-sm font-semibold">Auto-create users on first login</p>
              <p className="text-xs text-muted-foreground">Disable to require manual invitation before a user can log in.</p>
            </div>
            <Switch
              checked={!!cfg.jit_enabled}
              onCheckedChange={(v) => patch({ jit_enabled: v })}
              data-testid="jit-enabled-switch"
            />
          </div>

          <div className="space-y-1">
            <Label>Default role for new users</Label>
            <Select
              value={cfg.jit_default_role || 'viewer'}
              onValueChange={(v) => patch({ jit_default_role: v })}
            >
              <SelectTrigger data-testid="jit-default-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(cfg.allowed_jit_roles || ['viewer','team_member','auditor','analyst','user','admin','owner']).map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              First-time SSO users are assigned this role. Existing Beskar accounts keep their current role.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Attribute mapping */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">SAML attribute mapping</CardTitle>
          <CardDescription>
            Add every SAML attribute name your IdP might send. Beskar tries them in order.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AttributeMappingEditor
            mapping={cfg.attribute_mapping || cfg.default_attribute_mapping || {}}
            defaults={cfg.default_attribute_mapping || {}}
            onChange={(m) => patch({ attribute_mapping: m })}
          />
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-white/90 backdrop-blur border-t py-3 -mx-6 px-6">
        <Button type="button" variant="outline" onClick={load} data-testid="sso-reload-btn">
          <RefreshCw className="w-4 h-4 mr-2" />Reload
        </Button>
        <Button type="button" onClick={handleSave} disabled={saving} data-testid="sso-save-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-2" />Save SSO config</>}
        </Button>
      </div>
    </div>
  );
};

export default SamlSsoConfig;
